/**
 * Lightweight confetti burst — no external dependency. Spawns a full-screen
 * canvas overlay for ~2 seconds and animates 140 gold/champagne/pink pieces
 * falling with sideways drift + spin, then removes itself.
 *
 * Usage: `import fireConfetti from "../lib/confetti"; fireConfetti();`
 */
const COLORS = ["#FFD700", "#FFE58A", "#D4AF37", "#F2DC8C", "#E86A92", "#7CFC9A"];

export default function fireConfetti(opts = {}) {
  if (typeof window === "undefined") return;

  const canvas = document.createElement("canvas");
  canvas.style.cssText =
    "position:fixed;inset:0;pointer-events:none;z-index:2147483000;";
  document.body.appendChild(canvas);
  const ctx = canvas.getContext("2d");

  const dpr = window.devicePixelRatio || 1;
  const resize = () => {
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener("resize", resize);

  const count = opts.count ?? 140;
  const originX = opts.x ?? window.innerWidth / 2;
  const originY = opts.y ?? window.innerHeight / 2;
  const parts = Array.from({ length: count }, () => {
    const angle = Math.random() * Math.PI * 2;
    const speed = 5 + Math.random() * 8;
    return {
      x: originX,
      y: originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 6,       // initial upward kick
      size: 6 + Math.random() * 6,
      color: COLORS[(Math.random() * COLORS.length) | 0],
      rot: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.4,
      life: 0,
    };
  });

  const gravity = 0.32;
  const drag = 0.992;
  const maxLife = 130;

  let stopped = false;
  const step = () => {
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    let alive = 0;
    for (const p of parts) {
      p.vy += gravity;
      p.vx *= drag;
      p.vy *= drag;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.spin;
      p.life += 1;
      const opacity = Math.max(0, 1 - p.life / maxLife);
      if (opacity <= 0) continue;
      alive += 1;
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.fillStyle = p.color;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }
    if (alive > 0 && !stopped) requestAnimationFrame(step);
    else {
      window.removeEventListener("resize", resize);
      canvas.remove();
    }
  };
  requestAnimationFrame(step);

  // Hard-stop safety after 4s in case something interferes with rAF.
  setTimeout(() => {
    stopped = true;
    try { canvas.remove(); } catch { /* already gone */ }
  }, 4000);
}
